/**
 * Search-preset schemas — wire formats for the named filter+sort
 * bundles a user can switch between on the find-page gear menu.
 *
 * Three layers:
 *
 *   1. `SearchPresetSortSchema` — the sort slice (field + direction).
 *      Mirrors what `apps/web/src/routes/(app)/find/view-schema.ts`
 *      already uses for the local view-state; promoted here so both
 *      sides reference the same source of truth.
 *
 *   2. `SearchPresetFiltersSchema` — the filter slice. Same shape as
 *      `PreferencesSchema` minus `userId` (presets are scoped via the
 *      row's user_id, not the payload), and minus the cross-field
 *      refines (`ageMin <= ageMax`, `heightMinCm <= heightMaxCm`).
 *      Refines are dropped because a preset is a SNAPSHOT of intended
 *      values — the cross-field check runs at apply time when the
 *      filters flow through the regular `PATCH /me/preferences`
 *      endpoint, which validates via the full `PreferencesSchema`.
 *      Re-enforcing the refines here would only duplicate that gate.
 *
 *   3. `SearchPresetSchema` — the full row shape returned by the
 *      list endpoint. `SearchPresetInputSchema` is the create/update
 *      payload (no id, no timestamps).
 *
 * Naming: 1-20 characters, single-line, rejects control characters
 * via the `\p{Cc}` Unicode category. The 20-char cap keeps the gear-
 * menu pill from wrapping at common phone widths.
 */

import { z } from 'zod';
import { UuidSchema } from '../primitives.js';
import { PreferencesSchema } from './preferences.js';

// ─── Sort ─────────────────────────────────────────────────────────────

export const SearchPresetSortSchema = z.object({
  field: z.enum(['score', 'distance', 'recency', 'country']),
  direction: z.enum(['asc', 'desc']),
});
export type SearchPresetSort = z.infer<typeof SearchPresetSortSchema>;

// ─── Filters ──────────────────────────────────────────────────────────

// Filters are stored as an opaque JSON object — same shape the
// `/me/preferences` endpoint returns (minus `userId`), but we
// deliberately DON'T re-validate every field at the preset layer.
// Two reasons:
//
//   1. Cross-version forward compatibility. The live preferences
//      row can legitimately hold values from older enum versions
//      (e.g. a legacy `wantsChildrenRequired: 'unsure'` that
//      pre-dates a 2026 enum tightening). Re-running the strict
//      schema on save would reject those snapshots and lock the
//      user out of the feature — but the matcher reads the live
//      row as-is, so the data is functional.
//   2. The validation gate that actually matters is at *apply*
//      time, when the preset's filters PATCH the live preferences
//      via `EditablePreferencesSchema`. That's the single source
//      of truth for "is this filter set legal to use right now."
//      Re-checking here would only duplicate it.
//
// Reference the import so static analysis can confirm we still
// depend on PreferencesSchema for sanity even though we don't reach
// into its shape — if PreferencesSchema ever disappears, this file
// should fail to compile and prompt a re-think.
void PreferencesSchema;

export const SearchPresetFiltersSchema = z.record(z.string(), z.unknown());
export type SearchPresetFilters = z.infer<typeof SearchPresetFiltersSchema>;

// ─── Name ─────────────────────────────────────────────────────────────

const NAME_NO_CONTROL_CHARS = /^[^\p{Cc}]+$/u;

/** 1-20 chars, no control characters. Trims leading + trailing
 *  whitespace before validating so a user can type "  Casual " and
 *  have it stored as "Casual". Rejects ASCII control codes + the
 *  full Unicode Cc category (DEL, line/paragraph separators, etc.)
 *  which would break menu rendering. Letters, digits, spaces,
 *  punctuation, and emoji are all permitted. */
export const SearchPresetNameSchema = z
  .string()
  .transform((v) => v.trim())
  .pipe(
    z
      .string()
      .min(1, 'Name is required')
      .max(20, 'Name must be 20 characters or fewer')
      .regex(NAME_NO_CONTROL_CHARS, 'Name contains invalid characters'),
  );

// ─── Row + input shapes ──────────────────────────────────────────────

export const SearchPresetSchema = z.object({
  id: UuidSchema,
  userId: UuidSchema,
  name: SearchPresetNameSchema,
  filters: SearchPresetFiltersSchema,
  sort: SearchPresetSortSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  lastAppliedAt: z.string().datetime({ offset: true }).nullable(),
});
export type SearchPreset = z.infer<typeof SearchPresetSchema>;

/** Create or full-update payload. No userId (taken from the auth
 *  context), no timestamps (server-set). */
export const SearchPresetInputSchema = z.object({
  name: SearchPresetNameSchema,
  filters: SearchPresetFiltersSchema,
  sort: SearchPresetSortSchema,
});
export type SearchPresetInput = z.infer<typeof SearchPresetInputSchema>;

/** Partial update — used by the rename / re-bind flow. Every field
 *  is optional, but the request must include at least one field; the
 *  service layer rejects an empty patch with 400. */
export const SearchPresetPatchSchema = z
  .object({
    name: SearchPresetNameSchema.optional(),
    filters: SearchPresetFiltersSchema.optional(),
    sort: SearchPresetSortSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.filters !== undefined || v.sort !== undefined, {
    message: 'At least one of name, filters, or sort must be provided',
  });
export type SearchPresetPatch = z.infer<typeof SearchPresetPatchSchema>;

// ─── Caps ─────────────────────────────────────────────────────────────

/** Application-enforced cap on the number of presets per user.
 *  Server returns 409 `cap_reached` on create when the user already
 *  holds this many. Picked to keep the gear menu scannable; lift
 *  later if we ever see real demand. */
export const MAX_SEARCH_PRESETS_PER_USER = 5;
